import { formatCurrency, formatPercent } from 'utils';
import React from 'react';
import FactSheetLink from 'components/FactSheet/FactSheetLink';
import KiidDocumentButton from './KiidDocumentButton';
import { Tooltip, makeStyles } from '@material-ui/core';
import { formatDate } from 'components/IPS/PastPerformanceTable/utils';
import CircularProgressBar from 'components/Base/CircularProgressBar/CircularProgressBar';
import moment from 'moment';
import { comfortRangeConfig } from 'components/RiskMapping/utils';
let requestsInProgress = [] as any;

export const getRiskRatingByTerm = (ratings: any, term: number) => {
  const rating =
    ratings?.risks?.find((risk: any) => Number(risk.term) === Number(term)) ||
    null;

  if (!rating) {
    return null;
  }

  return Math.round(rating.riskRating);
};

export const getRiskRating = (row: any, data: any, fallback?: any) => {
  const prefix = row.type === 'f' ? 'f' : 'mp';
  return (
    data[`${prefix}-${row.id}`] ||
    fallback || {
      riskGroup: 10,
      risks: []
    }
  );
};

export const resetTableState = (key: any) => {
  localStorage.removeItem(`${key}_state`);
  return true;
};

export const deleteLocalKeyStorageFuzzy = (keyName: string) => {
  for (const key in localStorage) {
    if (key.includes(keyName)) {
      localStorage.removeItem(key);
      return true;
    }
  }
};

export const getPercentageObject = (key: string, nullable = false) => ({
  type: 'number',
  accessorFn: (row: any) => {
    const value = Number(row[key]);
    return nullable && !value ? null : Math.round(value * 10000) / 100;
  },
  Cell: ({ cell }: any) => {
    const value = cell.getValue();
    return value === null ? '-' : formatPercent(value, true);
  }
});

export const getStringObject = (key: string, filterFn = 'text') => {
  return {
    enableColumnFilterModes: false, //disable changing filter mode for this column
    // columnFilterModeOptions: ['between', 'greaterThan', 'lessThan'], //only allow these filter modes
    filterFn: filterFn,
    accessorFn: (row: any) => row[key] || '-'
  };
};

export const getCachableData = (
  investments: any,
  isCacheValid: boolean,
  setIsLoading: Function,
  getData: any,
  setCachedData: Function,
  setLocalData?: Function
) => {
  const functionString = getData.toString();
  const inProgress = requestsInProgress.includes(functionString);

  if (isCacheValid || inProgress) {
    return;
  }

  setIsLoading(true);
  requestsInProgress.push(functionString);

  getData()
    .then(({ data }: any) => {
      if (setLocalData) {
        setLocalData(data);
      }

      setCachedData({
        data,
        investments
      });
    })
    .finally(() => {
      requestsInProgress = requestsInProgress.filter(
        (item: string) => item !== functionString
      );
      setIsLoading(false);
    });
};

export const mapCipOptions = (options: any) => {
  const cipOptions = [] as any;
  Object.keys(options).forEach(key => {
    const item = options[key];

    const cipModelPortfolios = item.model_portfolios.map((mp: any) => ({
      value: mp.id,
      mp_id: mp.mp_id,
      name: mp.name,
      type: 'mp',
      custom: !mp.cip_category,
      mps_charge: mp.mps_charge
    }));

    const cipFunds = item.funds.map((fund: any) => ({
      ...fund,
      value: fund.id,
      type: 'f',
      custom: false
    }));

    cipOptions.push(...cipFunds, ...cipModelPortfolios);
  });

  return cipOptions;
};

const useStyles = makeStyles({
  riskScoreWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }
});

export const getExpandedColumns = () => {
  const years = Array.from({ length: 5 }, (_, i) =>
    moment()
      .subtract(i + 1, 'years')
      .year()
      .toString()
  );

  const numberStrings = ['one', 'two', 'three', 'four', 'five'];

  return [
    {
      accessorKey: 'initial_charge',
      header: 'Initial fee',
      ...getPercentageObject('initial_charge')
    },
    {
      accessorKey: 'trading_costs',
      header: 'Transaction costs',
      ...getPercentageObject('trading_costs')
    },
    {
      accessorKey: 'three_m_performance',
      header: '3M performance (cumulative)',
      ...getPercentageObject('three_m_performance')
    },
    {
      accessorKey: 'six_m_performance',
      header: '6M performance (cumulative)',
      ...getPercentageObject('six_m_performance')
    },
    {
      accessorKey: 'three_y_performace',
      header: '3Y performance (cumulative)',
      ...getPercentageObject('three_y_performace')
    },
    {
      accessorKey: 'annualised_one_y_performance',
      header: '1Y performance (annualised)',
      ...getPercentageObject('one_y_performace')
    },
    {
      accessorKey: 'annualised_three_y_performance',
      header: '3Y performance (annualised)',
      ...getPercentageObject('annualised_three_y_performance')
    },
    {
      accessorKey: 'annualised_five_y_performance',
      header: '5Y performance (annualised)',
      ...getPercentageObject('annualised_five_y_performance')
    },
    ...years.map((year, index) => {
      const key = `calendar_${numberStrings[index]}_y_performance`;
      return {
        accessorKey: key,
        header: `${year} performance`,
        ...getPercentageObject(key)
      };
    }),
    {
      accessorKey: 'maximum_three_y_drawdown',
      header: '3Y Max Drawdown',
      ...getPercentageObject('maximum_three_y_drawdown')
    },
    {
      accessorKey: 'maximum_five_y_drawdown',
      header: '5Y Max Drawdown',
      ...getPercentageObject('maximum_five_y_drawdown')
    },
    {
      accessorKey: 'three_y_alpha',
      header: '3Y Alpha',
      ...getPercentageObject('three_y_alpha')
    },
    {
      accessorKey: 'five_y_alpha',
      header: '5Y Alpha',
      ...getPercentageObject('five_y_alpha')
    },
    {
      accessorKey: 'three_y_beta',
      header: '3Y Beta',
      ...getPercentageObject('three_y_beta')
    },
    {
      accessorKey: 'five_y_beta',
      header: '5Y Beta',
      ...getPercentageObject('five_y_beta')
    },
    {
      accessorKey: 'one_y_information_ratio',
      header: '1Y Information ratio',
      ...getPercentageObject('one_y_information_ratio')
    },
    {
      accessorKey: 'three_y_information_ratio',
      header: '3Y Information ratio',
      ...getPercentageObject('three_y_information_ratio')
    },
    {
      accessorKey: 'five_y_information_ratio',
      header: '5Y Information ratio',
      ...getPercentageObject('five_y_information_ratio')
    },
    {
      accessorKey: 'one_y_r_squared',
      header: '1Y R-squared',
      ...getPercentageObject('one_y_r_squared')
    },
    {
      accessorKey: 'three_y_r_squared',
      header: '3Y R-squared',
      ...getPercentageObject('three_y_r_squared')
    },
    {
      accessorKey: 'five_y_r_squared',
      header: '5Y R-squared',
      ...getPercentageObject('five_y_r_squared')
    },
    {
      accessorKey: 'one_y_sharpe_ratio',
      header: '1Y Sharpe ratio',
      ...getPercentageObject('one_y_sharpe_ratio')
    },
    {
      accessorKey: 'three_y_sharpe_ratio',
      header: '3Y Sharpe ratio',
      ...getPercentageObject('three_y_sharpe_ratio')
    },
    {
      accessorKey: 'five_y_sharpe_ratio',
      header: '5Y Sharpe ratio',
      ...getPercentageObject('five_y_sharpe_ratio')
    },
    {
      accessorKey: 'three_y_standard_deviation',
      header: '3Y Volatility',
      ...getPercentageObject('three_y_standard_deviation')
    },
    {
      accessorKey: 'five_y_standard_deviation',
      header: '5Y Volatility',
      ...getPercentageObject('five_y_standard_deviation')
    }
  ].map((column: any) => ({ ...column, visible: false }));
};

interface ColumnOptions {
  riskProfiler?: string;
  includeAllocation?: boolean;
  includeExpanded?: boolean;
  riskRatings?: any;
  theme?: any;
  forceType?: boolean;
  options?: any;
  ocf_field?: string;
  classes?: any;
}

const getRiskColumns = ({
  riskProfiler,
  riskRatings,
  theme,
  classes = null
}: any) => {
  if (!classes) {
    classes = useStyles();
  }

  if (riskProfiler === 'EValue') {
    return [
      {
        accessorKey: 'risk_rating',
        header: 'Risk profile for 21+ years',
        type: 'number',
        accessorFn: (row: any) => {
          const riskRating = getRiskRatingByTerm(
            getRiskRating(row, riskRatings.data),
            25
          );

          return riskRating && riskRating;
        },
        Cell: ({ row }: any) => {
          const ratings = getRiskRating(row.original, riskRatings.data);

          const riskRating = getRiskRatingByTerm(ratings, 25);

          return (
            <>
              {riskRating ? (
                <div className={classes.riskScoreWrapper}>
                  <CircularProgressBar
                    value={(riskRating / ratings.riskGroup) * 100}
                    display={riskRating.toString()}
                    color={
                      theme.palette.baseColors.charts['asset-allocation'][
                        riskRating - 1
                      ]
                    }
                  />
                </div>
              ) : (
                '-'
              )}
            </>
          );
        }
      }
    ];
  }

  const defaultRiskData = {
    growth_asset_percentage: 60,
    ok_risk: { low: null, high: null }
  };

  return [
    {
      accessorKey: 'growth_assets',
      header: 'Growth assets',
      type: 'number',
      accessorFn: (row: any) => {
        const riskRating =
          row.riskRating ||
          getRiskRating(row, riskRatings.data, defaultRiskData);
        const value = Number(riskRating.growth_asset_percentage);
        return !value ? null : formatPercent(value, false, 0);
      },
      Cell: ({ cell }: any) => {
        const value = cell.getValue();
        return value === null ? '-' : formatPercent(value, false, 0);
      }
    },
    {
      accessorKey: 'risk_comfort_score',
      header: 'Risk comfort score',
      type: 'number',
      accessorFn: (row: any) => {
        const riskRating = getRiskRating(
          row,
          riskRatings.data,
          defaultRiskData
        );

        return `${riskRating.ok_risk?.low} - ${riskRating.ok_risk?.high}`;
      }
    }
  ];
};

export const getFundColumns = ({
  riskProfiler = 'EValue',
  includeAllocation = true,
  riskRatings = null,
  theme = null,
  includeExpanded = false,
  ocf_field = 'ocf',
  options = {}
}: ColumnOptions = {}) => {
  const columns = [
    {
      accessorKey: 'name',
      header: 'Name',
      ...getStringObject('name'),
      Cell: ({ row }: any) =>
        row.original.isin === 'CASH' ? (
          row.original.name
        ) : (
          <FactSheetLink investment={row.original} />
        )
    },
    includeAllocation
      ? {
          accessorKey: 'allocation',
          header: '%',
          ...getPercentageObject('allocation')
        }
      : null,
    {
      accessorKey: 'ia_sector',
      header: 'IA Sector',
      ...getStringObject('ia_sector')
    },
    {
      accessorKey: 'sector_name',
      header: 'Morningstar Sector',
      ...getStringObject('sector_name')
    },
    {
      accessorKey: ocf_field,
      header: 'OCF',
      ...getPercentageObject(ocf_field)
    },
    {
      accessorKey: 'one_y_performace',
      header: '1Y Performance (cumulative)',
      ...getPercentageObject('one_y_performace')
    },
    includeExpanded
      ? {
          accessorKey: 'five_y_performace',
          header: '5Y Performance (cumulative)',
          ...getPercentageObject('five_y_performace')
        }
      : null,
    ...(riskRatings
      ? getRiskColumns({
          riskProfiler,
          riskRatings,
          theme
        })
      : []),
    {
      accessorKey: 'annual_yield',
      header: 'Annual Yield',
      exportAccessorFn: (row: any) => {
        const value = row.annual_yield;
        return value === null ? '-' : formatPercent(value);
      },
      accessorFn: (row: any) =>
        Math.round(Number(row.annual_yield) * 100 * 100) / 100,
      Cell: ({ row }: any) => {
        return (
          <Tooltip title={`as of ${row.original.annual_yield_date}`} arrow>
            <span style={{ cursor: 'default' }}>
              {formatPercent(Number(row.original.annual_yield) * 100)}
            </span>
          </Tooltip>
        );
      }
    },
    {
      accessorKey: 'group_name',
      header: 'Provider',
      ...getStringObject('group_name')
    },
    includeExpanded
      ? {
          accessorKey: 'maximum_one_y_drawdown',
          header: '1Y Max Drawdown',
          ...getPercentageObject('maximum_one_y_drawdown')
        }
      : null,
    includeExpanded
      ? {
          accessorKey: 'one_y_alpha',
          header: '1Y Alpha',
          ...getPercentageObject('one_y_alpha')
        }
      : null,
    includeExpanded
      ? {
          accessorKey: 'one_y_standard_deviation',
          header: '1Y Volatility',
          ...getPercentageObject('one_y_standard_deviation')
        }
      : null,
    includeExpanded
      ? {
          accessorKey: 'one_y_beta',
          header: '1Y Beta',
          ...getPercentageObject('one_y_beta')
        }
      : null,
    {
      accessorKey: 'inception_date',
      header: 'Inception Date',
      Cell: ({ cell }: any) =>
        cell.getValue() === null ? '-' : formatDate(cell.getValue()),
      enableColumnFilter: false
    },
    {
      accessorKey: 'size',
      header: 'Fund Size',
      exportAccessorFn: (row: any) => {
        if (row.size === null) {
          return '-';
        }

        const sizeValue = Number(row.size);
        return sizeValue >= 1000000
          ? `${formatCurrency(sizeValue / 1000000)}m`
          : formatCurrency(sizeValue);
      },
      accessorFn: (row: any) =>
        Math.round((Number(row.size) / 1000000) * 100) / 100,
      Cell: ({ row }: any) => {
        if (row.original.size === null) {
          return '-';
        }

        const sizeValue = Number(row.original.size);

        return (
          <Tooltip title={`as of ${formatDate(row.original.size_date)}`} arrow>
            <span style={{ cursor: 'default' }}>
              {sizeValue >= 1000000
                ? `${formatCurrency(sizeValue / 1000000)}m`
                : formatCurrency(sizeValue)}
            </span>
          </Tooltip>
        );
      }
    },
    {
      accessorKey: 'investment_type',
      header: 'Investment Type',
      ...getStringObject('investment_type')
    },
    {
      accessorKey: 'leveraged',
      header: 'Leveraged Fund',
      filterVariant: 'select',
      enableColumnFilterModes: false, //disable changing filter mode for this column
      accessorFn: (row: any) => (row.leveraged ? 'Yes' : 'No')
    },
    {
      accessorKey: 'distribution_status',
      header: 'Distribution Status',
      filterVariant: 'multi-select',
      ...getStringObject('distribution_status')
    },
    {
      accessorKey: 'isin',
      header: 'ISIN',
      ...getStringObject('isin')
    },
    {
      accessorKey: 'base_currency',
      header: 'Currency',
      filterVariant: 'multi-select',
      visible: false,
      ...getStringObject('base_currency')
    },
    {
      accessorKey: 'kiid',
      header: 'KIID',
      enableColumnFilter: false,
      enableSorting: false,
      exportIgnore: true,
      Cell: ({ row }: any) => (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          {!row.original.private &&
          !row.original.mp_id &&
          row.original.isin !== 'CASH' ? (
            <KiidDocumentButton isin={row.original.isin} />
          ) : (
            '-'
          )}
        </div>
      )
    },
    {
      accessorKey: 'fact_sheet',
      header: 'Factsheet',
      enableColumnFilter: false,
      enableSorting: false,
      exportIgnore: true,
      Cell: ({ row }: any) => (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          {!row.original.private &&
          !row.original.mp_id &&
          row.original.isin !== 'CASH' ? (
            <FactSheetLink investment={row.original} iconOnly={true} />
          ) : (
            '-'
          )}
        </div>
      )
    }
  ];

  if (includeExpanded) {
    columns.push(...getExpandedColumns());
    columns.push({
      visible: false,
      accessorKey: 'equity_style',
      header: 'Morningstar equity style',
      accessorFn: (row: any) =>
        options.equity_style.find(
          (option: any) => option.value == row.equity_style
        )?.name,
      Cell: ({ row }: any) => {
        return (
          options.equity_style.find(
            (option: any) => option.value == row.original.equity_style
          )?.name || '-'
        );
      }
    } as any);
    columns.push({
      visible: false,
      accessorKey: 'fixed_income_style',
      header: 'Morningstar fixed income style',
      accessorFn: (row: any) =>
        options.fixed_income_style.find(
          (option: any) => option.value == row.fixed_income_style
        )?.name,
      Cell: ({ row }: any) => {
        return (
          options.fixed_income_style.find(
            (option: any) => option.value == row.original.fixed_income_style
          )?.name || '-'
        );
      }
    } as any);
  }

  return columns.filter(Boolean);
};

export const getModelPortfolioColumns = ({
  riskProfiler = 'EValue',
  includeAllocation = true,
  riskRatings = null,
  theme = null,
  includeExpanded = false,
  ocf_field = 'ocf',
  classes = null
}: ColumnOptions = {}) => {
  const columns = [
    {
      accessorKey: 'name',
      header: 'Name',
      ...getStringObject('name'),
      Cell: ({ row }: any) => (
        <div style={{ display: 'block' }}>
          {row.original.name} (
          <span
            style={{
              justifyContent: 'center',
              fontWeight: '600',
              color: '#4469a6',
              fontSize: '0.9rem',
              cursor: 'pointer'
            }}
          >
            View
          </span>
          )
        </div>
      )
    },
    includeAllocation
      ? {
          accessorKey: 'allocation',
          header: '%',
          ...getPercentageObject('allocation')
        }
      : null,
    {
      accessorKey: 'sector_name',
      header: 'Morningstar Sector',
      ...getStringObject('sector_name')
    },
    {
      accessorKey: ocf_field,
      header: 'OCF',
      ...getPercentageObject(ocf_field)
    },
    {
      accessorKey: 'one_y_performace',
      header: '1Y Performance (cumulative)',
      ...getPercentageObject('one_y_performace')
    },
    includeExpanded
      ? {
          accessorKey: 'five_y_performace',
          header: '5Y Performance (cumulative)',
          ...getPercentageObject('five_y_performace')
        }
      : null,
    ...(riskRatings
      ? getRiskColumns({
          riskProfiler,
          riskRatings,
          theme,
          classes
        })
      : []),
    {
      accessorKey: 'firm_name',
      header: 'Provider',
      ...getStringObject('firm_name')
    },
    includeExpanded
      ? {
          accessorKey: 'maximum_one_y_drawdown',
          header: '1Y Max Drawdown',
          ...getPercentageObject('maximum_one_y_drawdown')
        }
      : null,
    includeExpanded
      ? {
          accessorKey: 'one_y_alpha',
          header: '1Y Alpha',
          ...getPercentageObject('one_y_alpha')
        }
      : null,
    includeExpanded
      ? {
          accessorKey: 'one_y_standard_deviation',
          header: '1Y Volatility',
          ...getPercentageObject('one_y_standard_deviation')
        }
      : null,
    includeExpanded
      ? {
          accessorKey: 'one_y_beta',
          header: '1Y Beta',
          ...getPercentageObject('one_y_beta')
        }
      : null,
    {
      accessorKey: 'inception_date',
      header: 'Inception Date',
      accessorFn: (row: any) => formatDate(row.inception_date),
      enableColumnFilter: false
    },
    {
      accessorKey: 'base_currency',
      header: 'Currency',
      filterVariant: 'multi-select',
      ...getStringObject('base_currency')
    }
    // {
    //   accessorKey: 'type',
    //   header: 'Type',
    //   enableColumnFilter: false,
    //   enableSorting: false,
    //   exportIgnore: true,
    //   Cell: ({ row }: any) => (
    //     <div style={{ display: 'flex' }}>
    //       {forceType || row.original.mp_id ? 'Portfolio - ' : 'Fund'}
    //       {(forceType || row.original.mp_id) && (
    //         <div
    //           style={{
    //             justifyContent: 'center',
    //             fontWeight: '600',
    //             color: '#4469a6',
    //             fontSize: '0.9rem',
    //             cursor: 'pointer'
    //           }}
    //         >
    //           &nbsp; View
    //         </div>
    //       )}
    //     </div>
    //   )
    // }
  ];

  if (includeExpanded) {
    columns.push(...getExpandedColumns());
  }

  return columns.filter(Boolean);
};

export const getComfortRanges = (investments: any, data: any) => {
  return investments.map((investment: any) => {
    const bands = {} as any;
    const prefix = investment.type === 'f' ? 'f' : 'mp';
    const comfortRanges = data[`${prefix}-${investment.id}`];

    Object.keys(comfortRangeConfig).forEach((key: string) => {
      bands[comfortRangeConfig[key]] =
        comfortRanges && comfortRanges[key]
          ? comfortRanges[key].high - Math.max(0, comfortRanges[key].low - 1)
          : null;
    });

    return {
      name: investment.name,
      growth_assets: comfortRanges?.growth_asset_percentage,
      ...bands
    };
  });
};
